<pt-feature-info />
<pt-breadcrumbs class="bg-white" [breadcrumbs]="breadcrumbs" />
<div class="border-b border-solid border-slate-300"></div>

<div class="wrapper flex flex-col gap-4">
  <h1 class="mat-headline-4 !mb-0">Security</h1>
  <pr-information-box>
    Workspace Security settings allow you to enable IP blocking and/or block
    individual staff member access outside specified hours. Be cautious when
    configuring these settings to avoid accidentally blocking yourself or other
    essential users.
  </pr-information-box>
  <pr-practice-selector
    class="w-fit"
    subscriptSizing="dynamic"
    [practice]="organisation.practice$ | async"
    [brands]="organisation.userBrands$ | async"
    (practiceSelected)="practiceSelected($event)"
  />
  <mat-card appearance="outlined">
    <form [formGroup]="ipWhitelistForm">
      <mat-card-header class="mb-6 mt-2 flex justify-between">
        <mat-card-title>IP Whitelist</mat-card-title>
        <mat-slide-toggle formControlName="isEnabled">
          Enable
        </mat-slide-toggle>
      </mat-card-header>
      <mat-card-content>
        <div class="p-2">
          <div class="mb-4">
            <pr-information-box>
              The workspace IP whitelist applies to all users by default.
              Per-staff IP restrictions can be set in Staff Restrictions. To
              exempt Staff from IP restrictions completely, add them to the IP
              Exempt Staff list below.
            </pr-information-box>
          </div>
          <pt-multi-chip-input
            formControlName="whitelist"
            itemName="IP"
            label="Whitelisted IPs"
            [validatorFn]="isIpValidatorFn"
          >
            <span matHintContent class="text-red-500">
              Any IP addresses <strong>not</strong> listed will lose access to
              the Practice
            </span>
          </pt-multi-chip-input>
          <mat-form-field class="mt-4 w-full" subscriptSizing="dynamic">
            <mat-label>IP Exempt Staff</mat-label>
            <mat-select
              multiple
              [formControl]="ipExemptUsersCtrl"
              [compareWith]="compareNamedDocuments"
            >
              @for (staffer of practiceStaff$ | async; track staffer.ref.path) {
                <mat-option [value]="staffer.user">
                  {{ staffer.user.name }}
                </mat-option>
              }
            </mat-select>
            <mat-hint>
              These staff can sign in from any IP address, even when the
              whitelist is enabled.
            </mat-hint>
          </mat-form-field>
          @if (userIp$ | async; as ipAddress) {
            <div class="mat-caption mt-4">
              <span>Your public IP</span>
              <div class="flex items-center gap-2">
                <div class="font-bold">{{ ipAddress }}</div>
                <a
                  href="javascript:void(0);"
                  class="copy-icon"
                  (click)="copyIpToClipboard(ipAddress)"
                >
                  <mat-icon
                    matTooltip="Copy public IP to clipboard"
                    color="accent"
                  >
                    content_copy
                  </mat-icon>
                </a>
              </div>
            </div>
          }
        </div>
      </mat-card-content>
    </form>
  </mat-card>
  <mat-card appearance="outlined">
    <form [formGroup]="userRestrictionsForm">
      <mat-card-header class="mb-6 mt-2 flex justify-between">
        <mat-card-title>Staff Restrictions</mat-card-title>
        <mat-slide-toggle formControlName="isEnabled">
          Enable
        </mat-slide-toggle>
      </mat-card-header>
      <mat-card-content class="mt-2">
        <span>New Staff Restriction</span>
        <div class="flex items-start gap-2 p-2">
          <mat-form-field subscriptSizing="dynamic">
            <mat-label>Select Staff</mat-label>
            <mat-select multiple [formControl]="staffSelectorCtrl">
              @for (staffer of staffOptions$ | async; track staffer.ref.id) {
                <mat-option [value]="staffer">
                  {{ staffer.user.name }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            mat-flat-button
            color="primary"
            [disabled]="(canAdd$ | async) === false"
            (click)="addUserRestriction()"
          >
            Add
          </button>
        </div>
        <pr-user-restriction-form
          class="block px-2"
          [scheduleGroup]="scheduleGroup"
          [ipOverrideGroup]="ipOverrideGroup"
        />
      </mat-card-content>
    </form>

    @if (userRestrictionRows().length) {
      <mat-divider />
      <mat-list>
        @for (row of userRestrictionRows(); track row.restriction.user.ref.id) {
          <mat-list-item>
            <div class="flex items-center justify-between">
              <div>{{ row.restriction.user.name }}</div>

              <div class="flex items-center gap-1">
                <span class="text-slate-500">
                  {{ row.restriction.schedule | map: formatSchedule : this }}
                </span>
                @if (row.isIpExempt) {
                  <span
                    class="mat-caption ml-2 rounded bg-slate-100 px-2 py-0.5 text-slate-600"
                    matTooltip="This staff member is exempt from all IP restrictions"
                  >
                    IP exempt
                  </span>
                } @else if (row.restriction.ipWhitelist?.isEnabled) {
                  <mat-icon
                    class="icon-xs ml-2 text-slate-500"
                    fontSet="material-symbols-outlined"
                    [matTooltip]="
                      row.restriction.ipWhitelist
                        | map: ipWhitelistTooltip : this
                    "
                  >
                    computer
                  </mat-icon>
                }
                <button
                  mat-icon-button
                  class="ml-2"
                  matTooltip="Edit restriction"
                  (click)="editRestriction(row.restriction)"
                >
                  <mat-icon>edit</mat-icon>
                </button>
                <button
                  mat-icon-button
                  matTooltip="Remove restriction"
                  (click)="deleteRestriction(row.restriction)"
                >
                  <mat-icon class="text-warn-500">delete</mat-icon>
                </button>
              </div>
            </div>
          </mat-list-item>
        }
      </mat-list>
    }
  </mat-card>
  <mat-card appearance="outlined">
    <mat-card-header class="mb-6 mt-2 flex justify-between">
      <mat-card-title>Two-Factor Authentication (2FA)</mat-card-title>
      <mat-slide-toggle
        [checked]="isMultifactorEnabled$ | async"
        (change)="toggleMultifactor($event.checked)"
      >
        {{ (isMultifactorEnabled$ | async) ? 'Enabled' : 'Disabled' }}
      </mat-slide-toggle>
    </mat-card-header>
    <mat-card-content>
      <p class="text-sm text-gray-600">
        When enabled, all workspace users will be required to set up two-factor
        authentication the next time they sign in. They can choose an
        authenticator app or phone verification.
      </p>
    </mat-card-content>
  </mat-card>
  <pt-buttons-container>
    <button
      mat-raised-button
      color="primary"
      [disabled]="saveDisabled$ | async"
      (click)="save()"
    >
      Save
    </button>
  </pt-buttons-container>
</div>
